import { Link, useParams } from 'react-router-dom'
import { UserButton, useAuth } from '@clerk/clerk-react'
import { useEffect, useMemo, useState } from 'react'
import { trackEvent } from '../analytics'
import TransactionsTable from '../components/TransactionsTable'
import { EXPORT_FORMATS, type ExportFormat } from '../lib/exportFormats'

type Statement = {
  id: string
  filename: string
  status: 'uploaded' | 'processing' | 'completed' | 'failed'
  uploaded_at: number
  processed_at?: number | null
  opening_balance?: number | null
  closing_balance?: number | null
  currency?: string | null
  account_last4?: string | null
  confidence?: number | null
  needs_review?: number | null
}

type Transaction = {
  id: string
  transaction_date: string | null
  description: string | null
  debit: number | null
  credit: number | null
  balance: number | null
}

export default function StatementDetailsPage() {
  const { id } = useParams()
  const { getToken } = useAuth()
  const apiBase = useMemo(() => import.meta.env.VITE_API_URL || '/api/v1', [])

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statement, setStatement] = useState<Statement | null>(null)
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [exporting, setExporting] = useState<ExportFormat | null>(null)
  const [processing, setProcessing] = useState(false)
  const [progressStart, setProgressStart] = useState<number | null>(null)
  const [elapsed, setElapsed] = useState<number>(0)

  const formatDate = (ts?: number | null) => {
    if (!ts) return '-'
    const d = new Date((ts as number) * 1000)
    return d.toISOString().slice(0, 10)
  }

  const load = async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    try {
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const [sRes, tRes] = await Promise.all([
        fetch(`${apiBase}/statements/${id}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${apiBase}/statements/${id}/transactions`, { headers: { Authorization: `Bearer ${token}` } })
      ])
      if (!sRes.ok) throw new Error(await sRes.text())
      if (!tRes.ok) throw new Error(await tRes.text())
      const s = await sRes.json()
      const t = await tRes.json()
      setStatement(s.statement)
      setTransactions(t.transactions || [])
    } catch (e: any) {
      setError(e.message || 'Failed to load statement')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // Poll if processing
  useEffect(() => {
    if (!statement) return
    if (statement.status !== 'processing') return
    if (progressStart === null) setProgressStart(Date.now())
    const timer = setInterval(() => load(), 5000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statement?.status])

  // Elapsed timer for progress UI
  useEffect(() => {
    if (progressStart === null) return
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - progressStart) / 1000)), 1000)
    return () => clearInterval(t)
  }, [progressStart])

  const triggerProcess = async () => {
    if (!id) return
    try {
      setProcessing(true)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/process`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!res.ok) throw new Error(await res.text())
      setProgressStart(Date.now())
      trackEvent('extract_started', { statement_id: id, source: 'details' })
      await load()
    } catch (e: any) {
      alert(`Process failed: ${e.message}`)
    } finally {
      setProcessing(false)
    }
  }

  // Balance tie-out: verify opening + credits - debits equals the statement's
  // stated closing balance, plus a running-balance continuity re-check. All
  // integer-cent math to avoid float drift.
  const tieOut = useMemo(() => {
    if (!statement || statement.status !== 'completed' || transactions.length === 0) return null
    const cents = (v: number | null | undefined) => (v == null ? null : Math.round(v * 100))
    let credits = 0
    let debits = 0
    for (const t of transactions) {
      credits += cents(t.credit) ?? 0
      debits += cents(t.debit) ?? 0
    }
    const opening = cents(statement.opening_balance)
    const closing = cents(statement.closing_balance)
    let continuityBreaks = 0
    let prev: number | null = opening
    for (const t of transactions) {
      const bal = cents(t.balance)
      if (bal != null && prev != null) {
        const expected = prev + (cents(t.credit) ?? 0) - (cents(t.debit) ?? 0)
        if (Math.abs(expected - bal) > 1) continuityBreaks++
      }
      if (bal != null) prev = bal
    }
    if (opening == null || closing == null) {
      return { state: 'unavailable' as const, credits, debits, continuityBreaks }
    }
    const diff = opening + credits - debits - closing
    return {
      state: (Math.abs(diff) <= 1 && continuityBreaks === 0 ? 'reconciled' : 'discrepancy') as 'reconciled' | 'discrepancy',
      credits,
      debits,
      diff,
      continuityBreaks,
      opening,
      closing,
    }
  }, [statement, transactions])

  const fmtCents = (c: number) => `${(c / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

  const triggerExport = async (format: ExportFormat) => {
    if (!id) return
    try {
      setExporting(format)
      const token = await getToken()
      if (!token) throw new Error('Not authenticated')
      const res = await fetch(`${apiBase}/statements/${id}/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ format }),
      })
      if (!res.ok) throw new Error(await res.text())
      const dl = await fetch(`${apiBase}/statements/${id}/export/${format}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!dl.ok) throw new Error(await dl.text())
      const blob = await dl.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `statement-${id}.${format}`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      trackEvent('export', { format, statement_id: id })
    } catch (e: any) {
      trackEvent('export_error', {
        format,
        message: String(e?.message || e).slice(0, 120),
      })
      alert(`Export failed: ${e.message}`)
    } finally {
      setExporting(null)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <nav className="flex items-center justify-between">
            <Link to="/" className="text-2xl font-bold text-indigo-600">
              BankStatementReader
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="px-4 py-2 text-gray-700">
                Dashboard
              </Link>
              <UserButton />
            </div>
          </nav>
        </div>
      </header>

      {/* Main */}
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Statement Details</h1>
            <p className="text-gray-600">ID: {id}</p>
          </div>
          <div>
            <Link to="/dashboard" className="text-indigo-600 hover:text-indigo-700">← Back to Dashboard</Link>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 p-4 rounded">{error}</div>
        )}

        {loading || !statement ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Filename</div>
                  <div className="font-medium">{statement.filename}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Status</div>
                  <div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      statement.status === 'completed' ? 'bg-green-100 text-green-700' :
                      statement.status === 'failed' ? 'bg-red-100 text-red-700' :
                      statement.status === 'processing' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {statement.status}
                    </span>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Uploaded</div>
                  <div className="font-medium">{formatDate(statement.uploaded_at)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Processed</div>
                  <div className="font-medium">{formatDate(statement.processed_at)}</div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3 items-center">
                {(statement.status === 'uploaded' || statement.status === 'failed') && (
                  <button
                    onClick={triggerProcess}
                    disabled={processing}
                    className={`px-4 py-2 rounded text-white ${processing ? 'bg-gray-400' : 'bg-indigo-600 hover:bg-indigo-700'}`}
                  >
                    {processing ? 'Processing…' : (statement.status === 'failed' ? 'Re-process' : 'Process Now')}
                  </button>
                )}

                {statement.status === 'processing' && (
                  <div className="flex items-center gap-3 text-yellow-700">
                    <div className="h-4 w-4 border-2 border-yellow-400 border-t-transparent rounded-full animate-spin"></div>
                    <div className="w-56 h-2 bg-yellow-100 rounded overflow-hidden">
                      <div className="h-2 bg-yellow-400 animate-pulse w-full" />
                    </div>
                    <div className="text-sm">{elapsed}s elapsed</div>
                  </div>
                )}

                {EXPORT_FORMATS.map(({ format, label, hint }) => (
                  <button
                    key={format}
                    onClick={() => triggerExport(format)}
                    disabled={statement.status !== 'completed' || exporting !== null}
                    title={hint}
                    className={`px-4 py-2 rounded ${statement.status !== 'completed' ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 hover:bg-gray-200'}`}
                  >{exporting === format ? `${label}…` : label}</button>
                ))}
              </div>
            </div>

            {tieOut && (
              <div className={`rounded-lg shadow p-6 mb-6 border ${
                tieOut.state === 'reconciled' ? 'bg-green-50 border-green-200' :
                tieOut.state === 'discrepancy' ? 'bg-amber-50 border-amber-300' :
                'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-bold text-gray-900">Balance check</h2>
                  {tieOut.state === 'reconciled' && (
                    <span className="px-2 py-1 rounded text-xs bg-green-100 text-green-800 font-medium">Reconciled</span>
                  )}
                  {tieOut.state === 'discrepancy' && (
                    <span className="px-2 py-1 rounded text-xs bg-amber-100 text-amber-800 font-medium">Needs attention</span>
                  )}
                  {tieOut.state === 'unavailable' && (
                    <span className="px-2 py-1 rounded text-xs bg-gray-200 text-gray-700 font-medium">Balances not stated</span>
                  )}
                </div>
                <div className="mt-3 text-sm text-gray-700">
                  {tieOut.state !== 'unavailable' ? (
                    <p>
                      Opening {fmtCents(tieOut.opening!)} + credits {fmtCents(tieOut.credits)} − debits {fmtCents(tieOut.debits)} ={' '}
                      {fmtCents(tieOut.opening! + tieOut.credits - tieOut.debits)} vs stated closing {fmtCents(tieOut.closing!)}
                      {tieOut.state === 'discrepancy' && tieOut.diff != null && (
                        <span className="font-medium text-amber-800"> (off by {fmtCents(Math.abs(tieOut.diff))})</span>
                      )}
                      {tieOut.continuityBreaks > 0 && (
                        <span className="font-medium text-amber-800"> · {tieOut.continuityBreaks} running-balance break{tieOut.continuityBreaks > 1 ? 's' : ''}</span>
                      )}
                    </p>
                  ) : (
                    <p>
                      This statement did not state machine-readable opening/closing balances, so only totals are shown:
                      credits {fmtCents(tieOut.credits)}, debits {fmtCents(tieOut.debits)}. Verify against the original PDF before relying on the export.
                    </p>
                  )}
                  {tieOut.state === 'discrepancy' && (
                    <p className="mt-1">Compare the flagged rows against the original PDF before exporting.</p>
                  )}
                </div>
              </div>
            )}

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Transactions ({transactions.length})</h2>
                <button onClick={load} className="text-sm text-indigo-600 hover:text-indigo-700">Refresh</button>
              </div>
              {statement.status !== 'completed' && (
                <div className="mb-4 text-yellow-800 bg-yellow-50 border border-yellow-200 p-3 rounded">
                  {statement.status === 'processing' ? 'OCR is processing. This page will refresh periodically…' : 'Transactions will appear after processing.'}
                </div>
              )}
              <TransactionsTable transactions={transactions} />
            </div>
          </>
        )}
      </main>
    </div>
  )
}
